/**
 * Starting plans for the three templates — what a new video looks like before the agent fills it, and the fixtures
 * the renderer test uses. Media is passed in (Content Hub picks, product images) so nothing here points at a client.
 */
import { DEFAULT_THEME, type VideoAspect, type VideoMedia, type VideoSpec, type VideoTheme } from './spec';

export function creatorAdSample(o: { theme?: VideoTheme; aspect?: VideoAspect; clips?: VideoMedia[] } = {}): VideoSpec {
  const clips = o.clips || [];
  return {
    v: 1, title: 'Creator ad', template: 'creator-ad', aspect: o.aspect || '9:16', theme: o.theme || DEFAULT_THEME, captionStyle: 'bold',
    scenes: [
      { id: 'hook', kind: 'hook', duration: 1.8, text: 'You have never seen this before', emphasis: ['never'], media: clips[0] || null },
      ...clips.slice(0, 3).map((media, i) => ({
        id: `clip-${i + 1}`, kind: 'clip' as const, duration: 4, transition: i === 0 ? 'cut' as const : 'slide' as const, media,
        captions: [{ text: 'This changed my morning routine', start: 0.2, end: 1.9 }, { text: 'and honestly I did not expect it', start: 1.9, end: 3.8 }],
      })),
      ...(clips.length ? [] : [{ id: 'text-1', kind: 'text' as const, duration: 3, text: 'Pick creator clips from the Content Hub', eyebrow: 'Creator ad', tone: 'dark' as const }]),
      { id: 'end', kind: 'endcard', duration: 2.5, transition: 'fade', headline: 'Made with real creators', cta: 'Shop now', tone: 'dark' },
    ],
  };
}

export function brandMotionSample(o: { theme?: VideoTheme; aspect?: VideoAspect; product?: VideoMedia | null } = {}): VideoSpec {
  return {
    v: 1, title: 'Brand motion', template: 'brand-motion', aspect: o.aspect || '9:16', theme: o.theme || DEFAULT_THEME,
    scenes: [
      { id: 'hook', kind: 'hook', duration: 1.6, text: 'Something new is here', emphasis: ['new'], tone: 'brand' },
      { id: 'statement', kind: 'text', duration: 2.6, transition: 'wipe', eyebrow: 'Introducing', text: 'Built for the way you actually live', emphasis: ['actually'], tone: 'dark' },
      ...(o.product ? [{ id: 'product', kind: 'product' as const, duration: 3, transition: 'slide' as const, media: o.product, name: 'The product name', line: 'One line on why it matters', tone: 'light' as const }] : []),
      { id: 'proof', kind: 'stat', duration: 2.6, transition: 'fade', value: '98%', label: 'would recommend it to a friend', tone: 'dark' },
      { id: 'end', kind: 'endcard', duration: 2.4, transition: 'fade', headline: 'Available now', cta: 'Find out more', tone: 'brand' },
    ],
  };
}

export function campaignRecapSample(o: { theme?: VideoTheme; aspect?: VideoAspect; clips?: VideoMedia[] } = {}): VideoSpec {
  const clips = o.clips || [];
  return {
    v: 1, title: 'Campaign recap', template: 'campaign-recap', aspect: o.aspect || '9:16', theme: o.theme || DEFAULT_THEME, captionStyle: 'clean',
    scenes: [
      { id: 'open', kind: 'text', duration: 2.2, eyebrow: 'Campaign recap', text: 'Six weeks. Twelve creators. One story.', emphasis: ['One'], tone: 'dark' },
      { id: 'reach', kind: 'stat', duration: 2.4, transition: 'slide', value: '2.4M', label: 'people reached', tone: 'brand' },
      ...(clips[0] ? [{ id: 'top-post', kind: 'clip' as const, duration: 3.5, transition: 'fade' as const, media: clips[0], overlay: 'Top post' }] : []),
      { id: 'engagement', kind: 'stat', duration: 2.4, transition: 'slide', value: '6.8%', label: 'engagement rate', sub: 'above the category benchmark', tone: 'dark' },
      { id: 'voice', kind: 'quote', duration: 3, transition: 'fade', quote: 'Okay I need this in my life right now', author: 'a comment on the top post', tone: 'light' },
      { id: 'end', kind: 'endcard', duration: 2.4, transition: 'fade', headline: 'Thank you for building this with us', tone: 'dark' },
    ],
  };
}
